import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';

import {
  OPERATING_TEMPERATURE,
  POWER_SUPPLY_RIPPLE,
  GAIN,
  RESISTOR,
  TOLERANCE,
  TEMPCO,
  TEMPERATURE_RANGE,
  VOLTAGE_REFERENCE,
  DAC_TYPE,
  FIND_RESISTOR_NETWORK_PART,
} from '../utils/validations';
import PrettyNum from '@ebiz/pretty-num';
import Constants from '../utils/constants';

import cloneDeep from 'lodash.clonedeep';

export default class ValidationService extends Service {
  @service intl;

  @tracked errorsByTab = { lastUpdated: null };
  @tracked warnings = { lastUpdated: null };
  @tracked info = { lastUpdated: null };

  get errorsExistInModel() {
    return this.errorsExist(this.errorsByTab);
  }

  get errors() {
    if (!this.errorsByTab) return {};
    let errors = {};
    Object.keys(this.errorsByTab).forEach((key) => {
      if (key === 'lastUpdated') errors[key] = this.errorsByTab[key];
      else Object.assign(errors, this.errorsByTab[key]); // This would override any duplicates amongst tabs
    });

    return errors;
  }

  get appParamsTabErrors() {
    return this.getTabErrorsByTabIndex(Constants.TAB_INDEXES.APP_PARAMS);
  }

  get errorsExistInAppParamsTab() {
    return this.errorsExist(this.appParamsTabErrors);
  }

  get circuitDesignTabErrors() {
    return this.getTabErrorsByTabIndex(Constants.TAB_INDEXES.CIRCUIT_DESIGN);
  }

  get errorsExistInCircuitDesignTab() {
    return this.errorsExist(this.circuitDesignTabErrors);
  }

  validateModel(model) {
    const displayFunction = (value) => {
        return PrettyNum.eng3(value);
      },
      selectedDac = model.signalChainHandler.dac,
      minOperatingTemperatureMetadata = {
        fieldName: this.intl.t('labels.params.operating_temperature'),
        suffix: this.intl.t('labels.units.celsius'),
        key: 'minOperatingTemperature',
        tab: Constants.TAB_INDEXES.APP_PARAMS,
      },
      maxOperatingTemperatureMetadata = {
        fieldName: this.intl.t('labels.params.operating_temperature'),
        suffix: this.intl.t('labels.units.celsius'),
        key: 'maxOperatingTemperature',
        tab: Constants.TAB_INDEXES.APP_PARAMS,
      },
      powerSupplyRippleMetadata = {
        displayFn: displayFunction,
        fieldName: this.intl.t('labels.params.power_supply_ripple'),
        suffix: this.intl.t('labels.units.v'),
        key: 'powerSupplyRipple',
        tab: Constants.TAB_INDEXES.APP_PARAMS,
      },
      temperatureRangeMetadata = {
        fieldName: this.intl.t('labels.params.operating_temperature'),
        suffix: this.intl.t('labels.units.celsius'),
        showAllInvalid: true,
        tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
      },
      dacTypeMetadata = {
        key: 'dacType',
        isWarning: true,
      };
    let voltageReferenceMetadata = {
        displayFn: displayFunction,
        suffix: this.intl.t('labels.units.v'),
        typeMap: {
          positive: selectedDac.type.isSinglePath
            ? this.intl.t('labels.voltage')
            : this.intl.t('labels.positive_voltage'),
          negative: selectedDac.type.isSinglePath
            ? this.intl.t('labels.voltage')
            : this.intl.t('labels.negative_voltage'),
        },
        showAllInvalid: true,
        tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
      },
      validations = [
        OPERATING_TEMPERATURE(
          model.signalChainHandler.dac.data.operatingTemperature.min,
          minOperatingTemperatureMetadata,
          model,
        ),
        OPERATING_TEMPERATURE(
          model.signalChainHandler.dac.data.operatingTemperature.max,
          maxOperatingTemperatureMetadata,
          model,
        ),
        POWER_SUPPLY_RIPPLE(model.signalChainHandler.dac.data.powerSupplyRipple, powerSupplyRippleMetadata, model),
        DAC_TYPE(dacTypeMetadata, model),
      ];

    model.signalChainHandler.currentItems
      .filter((signalChainItem) => signalChainItem.selected)
      .forEach((signalChainItem) => {
        validations.push(
          TEMPERATURE_RANGE(
            signalChainItem.selectedTemperatureRange,
            Object.assign(
              {
                key: signalChainItem.config.propertyName,
                itemName: this.intl.t(`labels.parts.${signalChainItem.config.appName}`),
              },
              temperatureRangeMetadata,
            ),
            model,
          ),
        );
      });

    let hideVoltageRefAmplifierError = false;
    model.resistorHandler.currentResistors
      .filter((resistor) => resistor.requiresValidation)
      .forEach((resistor) => {
        const res1Arg = this.intl.t(`labels.${resistor.res1Type}`),
          res2Arg = this.intl.t(`labels.${resistor.res2Type}`),
          gainMetadata = {
            displayFn: displayFunction,
            suffix: this.intl.t('labels.units.v'),
            key: `${resistor.propertyName}Gain`,
            tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
          },
          res1Metadata = {
            fieldName: res1Arg,
            suffix: this.intl.t('labels.units.ohm'),
            displayFn: displayFunction,
            key: `${resistor.propertyName}Res1`,
            tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
          },
          res2Metadata = {
            fieldName: res2Arg,
            suffix: this.intl.t('labels.units.ohm'),
            displayFn: displayFunction,
            key: `${resistor.propertyName}Res2`,
            tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
          },
          toleranceMetadata = {
            fieldName: this.intl.t('labels.tolerance', {
              res1: res1Arg,
              res2: res2Arg,
            }),
            suffix: this.intl.t('labels.units.percent'),
            key: `${resistor.propertyName}Tolerance`,
            tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
          },
          tempcoMetadata = {
            fieldName: this.intl.t('labels.tempco', {
              res1: res1Arg,
              res2: res2Arg,
            }),
            suffix: this.intl.t('labels.units.ppm_celsius'),
            key: `${resistor.propertyName}Tempco`,
            tab: Constants.TAB_INDEXES.CIRCUIT_DESIGN,
          },
          resistorNetworkMetadata = {
            key: `${resistor.propertyName}NetworkPart`,
            isInfo: true,
          };

        if (resistor.requiresGainValidation) {
          hideVoltageRefAmplifierError = true;
          validations.push(GAIN(resistor, gainMetadata, model));
        }
        if (resistor.findResistorNetworkPart)
          validations.push(FIND_RESISTOR_NETWORK_PART(resistor, resistorNetworkMetadata, model));
        if (resistor.res1Type !== 'empty_string') validations.push(RESISTOR(resistor.res1, res1Metadata, model));
        if (resistor.res2Type !== 'empty_string') validations.push(RESISTOR(resistor.res2, res2Metadata, model));
        validations.push(TOLERANCE(resistor.tolerance, toleranceMetadata, model));
        validations.push(TEMPCO(resistor.tempco, tempcoMetadata, model));
      });

    if (
      model.signalChainHandler.voltageReference &&
      !model.signalChainHandler.voltageReference.isInternal &&
      !hideVoltageRefAmplifierError
    ) {
      voltageReferenceMetadata.key = model.signalChainHandler.voltageReference.config.propertyName;
      validations.push(VOLTAGE_REFERENCE(voltageReferenceMetadata, model));
    }

    let newErrors = { lastUpdated: new Date() };
    const newWarnings = { lastUpdated: new Date() },
      newInfo = { lastUpdated: new Date() };

    validations.forEach((validation) => {
      const key = validation.key;

      if (validation.isWarning) this.addMessagesIfFound(newWarnings, key, validation.results, (item) => !item.isValid);
      else if (validation.isInfo) this.addMessagesIfFound(newInfo, key, validation.results, (item) => item.isValid);
      else {
        const tab = validation.tab;

        if (validation.showAllInvalid) {
          const invalidResults = validation.results.filter((item) => !item.isValid);
          if (invalidResults.length) {
            if (!(tab in newErrors)) newErrors[tab] = {};
            if (!(key in newErrors[tab])) newErrors[tab][key] = [];
            newErrors[tab][key].push(
              ...invalidResults.map((item) => htmlSafe(this.intl.t(item.translation, item.translationArgs))),
            );
          }
        } else {
          const invalidResult = validation.results.find((item) => !item.isValid);
          if (invalidResult) {
            if (!(tab in newErrors)) newErrors[tab] = {};
            newErrors[tab][key] = htmlSafe(this.intl.t(invalidResult.translation, invalidResult.translationArgs));
          }
        }
      }
    });
    this.errorsByTab = newErrors;
    this.warnings = newWarnings;
    this.info = newInfo;
  }

  addMessagesIfFound(result, key, checkList, checkFn) {
    const found = checkList
      .filter((item) => checkFn(item))
      .map((item) => htmlSafe(this.intl.t(item.translation, item.translationArgs)));
    if (found.length) result[key] = found;
  }

  getTabErrorsByTabIndex(tabIndex) {
    if (!this.errorsByTab) return {};
    let tabErrors = tabIndex in this.errorsByTab ? cloneDeep(this.errorsByTab[tabIndex]) : {};
    tabErrors.lastUpdated = this.errorsByTab.lastUpdated;
    return tabErrors;
  }

  errorsExist(errors) {
    if (!errors) return false;

    const errorKeys = Object.keys(errors),
      numberOfErrors = 'lastUpdated' in errors ? errorKeys.length - 1 : errorKeys.length;

    return numberOfErrors > 0;
  }

  errorsExistByTabIndex(tabIndex) {
    const errors = this.getTabErrorsByTabIndex(tabIndex);
    return this.errorsExist(errors);
  }
}
